import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError, type AccountRow, type Me, type Portal, type UsageResponse } from '@/api/client';
import { AccountsTable } from '@/components/AccountsTable';
import { DateRangeControl } from '@/components/DateRangeControl';
import { MessageVolumeChart } from '@/components/MessageVolumeChart';
import { SummaryCards } from '@/components/SummaryCards';
import { TenantFilter } from '@/components/TenantFilter';
import { TicketsBoard } from '@/components/TicketsBoard';
import { UpsellTrends } from '@/components/UpsellTrends';
import { UsageTable } from '@/components/UsageTable';
import { PORTAL_COPY } from '@/lib/portal';
import { daysAgo, fmtDate, ymd } from '@/lib/format';
import { BestyMark } from '@/components/BestyMark';
import { Button, Card, ErrorNote, LinkButton, SectionHeader, SegmentedTabs, Spinner } from '@/ui';

const SIGNUP_URL = 'https://app.getbesty.ai/signup?source=onboarding';

type Tab = 'overview' | 'upsells' | 'tickets';

export function Dashboard({ me, portal, onLogout }: { me: Me; portal: Portal; onLogout: () => void }) {
  const copy = PORTAL_COPY[portal.key];
  const tabs: Array<{ key: Tab; label: string }> = [
    { key: 'overview', label: 'Overview' },
    { key: 'upsells', label: 'Upsells' },
    ...(portal.tickets ? [{ key: 'tickets' as const, label: 'Tickets' }] : []),
  ];
  const [start, setStart] = useState(ymd(daysAgo(29)));
  const [end, setEnd] = useState(ymd(new Date()));
  const [accounts, setAccounts] = useState<AccountRow[] | null>(null);
  const [usage, setUsage] = useState<UsageResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [appliedRange, setAppliedRange] = useState({ start, end });
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [appliedSelectedIds, setAppliedSelectedIds] = useState<number[]>([]);
  const [tab, setTab] = useState<Tab>('overview');
  const [reloadKey, setReloadKey] = useState(0);
  const requestIdRef = useRef(0);
  const selectionDebounceRef = useRef<number | null>(null);

  const load = useCallback(
    async (requestStart: string, requestEnd: string, requestSelectedIds: number[], reloadAccounts = false) => {
      const requestId = ++requestIdRef.current;
      setLoading(true);
      setError(null);
      try {
        let selectedIdsForUsage = requestSelectedIds;
        if (reloadAccounts) {
          const a = await api.accounts(portal.key);
          if (requestId !== requestIdRef.current) return;
          setAccounts(a.rows);
          // An un-sanctioned location must leave the selection, or /api/usage 403s on every refresh.
          // Dropping the last selected location falls back to all locations, since empty means all.
          const sanctionedIds = new Set(a.rows.map((account) => account.userId));
          selectedIdsForUsage = requestSelectedIds.filter((userId) => sanctionedIds.has(userId));
          setSelectedIds(selectedIdsForUsage);
        }
        const u = await api.usage(portal.key, requestStart, requestEnd, selectedIdsForUsage);
        if (requestId !== requestIdRef.current) return;
        setUsage(u);
        setAppliedRange({ start: requestStart, end: requestEnd });
        setAppliedSelectedIds(selectedIdsForUsage);
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        if (err instanceof ApiError && err.status === 401) {
          window.location.reload();
          return;
        }
        setError('Could not load the latest numbers. Try again in a moment.');
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    },
    [portal.key]
  );

  useEffect(() => {
    void load(start, end, selectedIds, true);
    return () => {
      if (selectionDebounceRef.current !== null) window.clearTimeout(selectionDebounceRef.current);
    };
  }, [load]);

  function applyRange(nextStart: string, nextEnd: string) {
    if (selectionDebounceRef.current !== null) {
      window.clearTimeout(selectionDebounceRef.current);
      selectionDebounceRef.current = null;
    }
    setStart(nextStart);
    setEnd(nextEnd);
    void load(nextStart, nextEnd, selectedIds);
  }

  function applySelection(nextSelectedIds: number[]) {
    setSelectedIds(nextSelectedIds);
    if (selectionDebounceRef.current !== null) window.clearTimeout(selectionDebounceRef.current);
    selectionDebounceRef.current = window.setTimeout(() => {
      selectionDebounceRef.current = null;
      void load(start, end, nextSelectedIds);
    }, 250);
  }

  function refresh() {
    if (selectionDebounceRef.current !== null) {
      window.clearTimeout(selectionDebounceRef.current);
      selectionDebounceRef.current = null;
    }
    setReloadKey((key) => key + 1);
    void load(start, end, selectedIds, true);
  }

  const rangeLabel = `${fmtDate(appliedRange.start)} to ${fmtDate(appliedRange.end)}`;
  const scopeLabel =
    appliedSelectedIds.length === 0
      ? 'All locations'
      : `${appliedSelectedIds.length} of ${accounts?.length ?? 0} locations`;
  const appliedLabel = `${scopeLabel}, ${rangeLabel}`;
  const visibleAccounts =
    accounts && appliedSelectedIds.length > 0
      ? accounts.filter((account) => appliedSelectedIds.includes(account.userId))
      : accounts;

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-20 border-b border-neutral-200/70 bg-white/85 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-[1440px] items-center justify-between gap-3 px-8">
          <div className="flex items-center gap-3">
            <BestyMark size={20} className="text-neutral-950" />
            <span className="h-4 w-px bg-neutral-200" aria-hidden />
            <span className="text-[14px] font-medium text-neutral-950">{copy.name}</span>
          </div>
          <div className="flex items-center gap-3">
            {portal.key === 'skyrun' && (
              <LinkButton href={SIGNUP_URL} variant="primary">
                Create new account
              </LinkButton>
            )}
            <span className="text-[13px] text-neutral-500">{me.email}</span>
            <Button variant="ghost" onClick={onLogout}>
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1440px] px-8 pt-10">
        <div className="flex flex-wrap items-end justify-between gap-6 border-b border-neutral-200/70 pb-6">
          <div>
            <h1 className="font-serif text-[34px] leading-[1.15] font-normal tracking-[-0.02em] text-neutral-950">
              {copy.name}
            </h1>
            <p className="mt-2 text-[14px] text-neutral-500">{copy.tagline}</p>
          </div>
          <SegmentedTabs options={tabs} value={tab} onChange={setTab} />
        </div>
      </div>

      {tab !== 'tickets' ? (
        <main className="mx-auto max-w-[1440px] space-y-6 px-8 py-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              {accounts && <TenantFilter accounts={accounts} selectedIds={selectedIds} onChange={applySelection} />}
              <DateRangeControl start={start} end={end} onChange={applyRange} loading={loading} />
            </div>
            <Button variant="secondary" disabled={loading} onClick={refresh}>
              {loading ? 'Refreshing' : 'Refresh'}
            </Button>
          </div>

          {error && <ErrorNote>{error}</ErrorNote>}

          <SummaryCards
            accounts={visibleAccounts ?? null}
            totals={usage?.totals ?? null}
            variant={portal.key === 'skyrun' && tab === 'overview' ? 'messaging' : 'upsells'}
          />

          {tab === 'upsells' ? (
            accounts ? (
              <UpsellTrends
                portal={portal.key}
                accounts={accounts}
                start={appliedRange.start}
                end={appliedRange.end}
                userIds={appliedSelectedIds}
                reloadKey={reloadKey}
              />
            ) : (
              <Card className="p-4">
                <Spinner label="Loading locations" />
              </Card>
            )
          ) : (
            <>
              <Card>
                <SectionHeader title="Daily message volume" subtitle={appliedLabel} />
                {usage ? <MessageVolumeChart daily={usage.daily} /> : <Spinner label="Loading messages" />}
              </Card>

              {accounts ? (
                <AccountsTable rows={visibleAccounts ?? []} portal={portal} />
              ) : (
                <Card className="p-4">
                  <Spinner label="Loading locations" />
                </Card>
              )}

              {usage ? (
                <UsageTable rows={usage.rows} rangeLabel={appliedLabel} selectedCount={appliedSelectedIds.length} />
              ) : (
                <Card className="p-4">
                  <Spinner label="Loading usage" />
                </Card>
              )}
            </>
          )}

          <p className="pb-10 text-[12px] text-neutral-400">
            Revenue figures are gross upsell revenue collected in the selected date range, by location.
          </p>
        </main>
      ) : (
        <main className="mx-auto max-w-[1440px] px-8 py-6">
          <TicketsBoard />
        </main>
      )}
    </div>
  );
}
